import { IFilter } from "@saleor/components/Filter";
import { AutocompleteFilterOpts, FilterOpts, MinMax } from "@saleor/types";
import {
  createAutocompleteField,
  createDateField,
  createOptionsField
} from "@saleor/utils/filters/fields";
import { defineMessages, IntlShape } from "react-intl";

import { FinanceOrderListUrlFilters } from "../../urls";

export enum FinanceOrderFilterKeys {
  mall = "malll",
  brand = "brand",
  orderDate = "orderDate",
  completedAt = "completedAt",
  isPayoutEligible = "isPayoutEligible",
  paymentMethodType = "paymentMethodType"
}

export enum PaymentType {
  CASH = "cash",
  CARD = "card"
}

export enum FinanceOrderBoolean {
  true = "true",
  false = "false"
}

export interface FinanceOrderListFilterOpts {
  brand: FilterOpts<string[]> & AutocompleteFilterOpts;
  mall: FilterOpts<string[]> & AutocompleteFilterOpts;
  orderDate: FilterOpts<MinMax>;
  completedAt: FilterOpts<MinMax>;
  isPayoutEligible: FilterOpts<string>;
  paymentMethodType: FilterOpts<string>;
}

const messages = defineMessages({
  brand: {
    defaultMessage: "Brand",
    description: "brand"
  },
  card: {
    defaultMessage: "Card",
    description: "card"
  },
  cash: {
    defaultMessage: "Cash",
    description: "cash"
  },
  completed: {
    defaultMessage: "Order Completion Date",
    description: "order"
  },
  created: {
    defaultMessage: "Order Created Date",
    description: "order"
  },
  false: {
    defaultMessage: "FALSE",
    description: "false"
  },
  isPayoutEligible: {
    defaultMessage: "Payout Eligibility",
    description: "payout eligibility"
  },
  mall: {
    defaultMessage: "Mall",
    description: "mall"
  },
  paymentMethodType: {
    defaultMessage: "Payment Method",
    description: "payment Method"
  },
  storeOrderCode: {
    defaultMessage: "Store Order Code",
    description: "store order code"
  },
  true: {
    defaultMessage: "TRUE",
    description: "true"
  }
});

export function createFilterStructure(
  intl: IntlShape,
  opts: FinanceOrderListFilterOpts,
  params: FinanceOrderListUrlFilters
): IFilter<FinanceOrderFilterKeys> {
  return params.mall
    ? [
        {
          ...createAutocompleteField(
            FinanceOrderFilterKeys.brand,
            intl.formatMessage(messages.brand),
            opts.brand.value,
            opts.brand.displayValues,
            true,
            opts.brand.choices,
            {
              hasMore: opts.brand.hasMore,
              initialSearch: "",
              loading: opts.brand.loading,
              onFetchMore: opts.brand.onFetchMore,
              onSearchChange: opts.brand.onSearchChange
            }
          ),
          active: opts.brand.active
        },
        {
          ...createAutocompleteField(
            FinanceOrderFilterKeys.mall,
            intl.formatMessage(messages.mall),
            opts.mall.value,
            opts.mall.displayValues,
            false,
            opts.mall.choices,
            {
              hasMore: opts.mall.hasMore,
              initialSearch: "",
              loading: opts.mall.loading,
              onFetchMore: opts.mall.onFetchMore,
              onSearchChange: opts.mall.onSearchChange
            }
          ),
          active: opts.mall.active
        },
        {
          ...createDateField(
            FinanceOrderFilterKeys.orderDate,
            intl.formatMessage(messages.created),
            opts.orderDate.value
          ),
          active: opts.orderDate.active
        },
        {
          ...createDateField(
            FinanceOrderFilterKeys.completedAt,
            intl.formatMessage(messages.completed),
            opts.completedAt.value
          ),
          active: opts.completedAt.active
        },
        {
          ...createOptionsField(
            FinanceOrderFilterKeys.isPayoutEligible,
            intl.formatMessage(messages.isPayoutEligible),
            [opts.isPayoutEligible.value],
            false,
            [
              {
                label: intl.formatMessage(messages.true),
                value: FinanceOrderBoolean.true
              },
              {
                label: intl.formatMessage(messages.false),
                value: FinanceOrderBoolean.false
              }
            ]
          ),
          active: opts.isPayoutEligible.active
        },
        {
          ...createOptionsField(
            FinanceOrderFilterKeys.paymentMethodType,
            intl.formatMessage(messages.paymentMethodType),
            [opts.paymentMethodType.value],
            false,
            [
              {
                label: intl.formatMessage(messages.card),
                value: PaymentType.CARD
              },
              {
                label: intl.formatMessage(messages.cash),
                value: PaymentType.CASH
              }
            ]
          ),
          active: opts.paymentMethodType.active
        }
      ]
    : [
        {
          ...createAutocompleteField(
            FinanceOrderFilterKeys.mall,
            intl.formatMessage(messages.mall),
            opts.mall.value,
            opts.mall.displayValues,
            false,
            opts.mall.choices,
            {
              hasMore: opts.mall.hasMore,
              initialSearch: "",
              loading: opts.mall.loading,
              onFetchMore: opts.mall.onFetchMore,
              onSearchChange: opts.mall.onSearchChange
            }
          ),
          active: opts.mall.active
        }
      ];
}
